// packages/web-app/src/services/contextService.ts
import type { CandidateBallotpediaBlock } from '@/types/ballotpedia';
import { apiClient } from '@/utils/apiClient';
import { getLikedCandidateIds } from '@/hooks/useLikedCandidates';
import { getMeasureSelections } from '@/hooks/useMeasureSelections';
import { holdChatUntil } from '@/hooks/useBallotLoading';

export { CONTEXT_VERSION } from './contextVersion';

export interface ContextCandidate {
  fullName: string;
  // Democracy Works' candidate id, when the record has one — the same id
  // `votemate_liked_candidates` keys its picks on, so a like made
  // mid-conversation can be matched back to a name in chat context.
  id?: string;
  party?: string | null;
  isIncumbent?: boolean | null;
  status?: string | null;
  // Ballotpedia provenance, mirroring the backend ``ContextCandidate``.
  // ``ballotpediaUrl`` is the citable article link the ballot record carries —
  // the only copy for a candidate whose ``bp`` block was pruned as a duplicate,
  // and for a Ballotpedia-only candidate that has no block at all.
  ballotpediaUrl?: string | null;
  // 'ballotpedia' when Ballotpedia listed the candidate and Democracy Works
  // did not, so chat attributes the name rather than implying the DW roster
  // carried it.
  source?: string;
  // Ballotpedia enrichment. The signed-in context carries the whole block; a
  // guest-built one carries the two fields chat needs to say whether a name is
  // on the ballot or a write-in.
  bp?: Partial<CandidateBallotpediaBlock> | null;
}

export interface ContextVotingDates {
  election_day?: string;
  // Registration deadlines. The *_postmarked_or_received flag tells voters
  // whether mail registrations count by postmark date vs. receipt date —
  // this can be a several-day difference at the deadline.
  registration_online_date?: string;
  registration_mail_date?: string;
  registration_mail_postmarked_or_received?: string;
  registration_in_person_date?: string;
  registration_same_day?: boolean;
  // Early voting window. early_voting_varies = hours/days vary by location;
  // when true, prefer surfacing early_voting_url over a uniform schedule.
  early_voting_start?: string;
  early_voting_end?: string;
  early_voting_varies?: boolean;
  early_voting_url?: string;
  // Absentee. absentee_postmarked_or_received: same nuance as registration.
  absentee_request_date?: string;
  absentee_return_mail_date?: string;
  absentee_postmarked_or_received?: string;
  absentee_return_in_person_date?: string;
  // All-mail states (CO, OR, WA, etc.): when ballots are sent.
  all_mail_send_start?: string;
  polls_close_time?: string;
}

export interface ContextPollingLocation {
  election_date?: string;
  election_name?: string;
  polling_location_url: string;
}

export interface ContextElectionOfficeLink {
  url: string;
  label?: string;
}

export interface ContextElectionOffice {
  name: string;
  level?: 'local' | 'state' | string;
  links: ContextElectionOfficeLink[];
}

// One reach-out channel for an officeholder (Ballotpedia ``collections=contact``).
// ``type`` is "Email" | "Phone" | "Website".
export interface ContextOfficeholderContact {
  type: string;
  value: string;
}

export interface ContextOfficeholder {
  name: string;
  office_name?: string;
  office_level?: string;
  office_branch?: string;
  district_name?: string;
  party?: string;
  status?: string;
  date_assumed_office?: string;
  date_term_ends?: string;
  url?: string;
  // Phone/email/website for the office. Carried into chat so VoteMate can tell
  // a voter who to contact about a service problem, not just who holds a seat.
  contacts?: ContextOfficeholderContact[];
}

export interface ConversationContext {
  v: number;
  user: {
    id: string;
    preferred_language?: string;
    state?: string;
    county?: string;
    zip_code?: string;
    source_type?: string;
  };
  authorities: {
    timezone?: string;
    polling_location_url?: string;
    registration_status_url?: string;
    election_office_url?: string;
    source_type?: string;
  };
  election: {
    next_election_date?: string;
    elections: Array<{
      election_date: string;
      election_name: string;
      election_type: string;
      // Democracy Works flag: "expected", "official", etc.
      guidance_phase?: string;
      contest_count: number;
      contests: Array<{
        id: string;
        title: string;
        // Democracy Works' descriptive contest label, as the Ballot Preview
        // shows it ("… school board At-large"); `title` is the office alone.
        name?: string;
        level?: string;
        district_name?: string;
        ocd_id?: string;
        seats?: number;
        // Set on partisan primary contests to the party whose primary
        // ballot the contest appears on (e.g. "Democratic" / "Republican").
        // Same office appears as TWO contest entries during a partisan
        // primary — one per party — because voters only participate in one.
        partisan_primary?: string;
        // 'ballotpedia' on an ultralocal race Ballotpedia listed and Democracy
        // Works did not return, so chat attributes the race to Ballotpedia.
        source?: string;
        // 'special' on a special election, which can share its name with the
        // office's regular election on the same ballot.
        contest_type?: string;
        // Ballotpedia's stage for this race ('general' / 'primary' / …), which
        // can differ from the election's name on a mixed day.
        stage?: string;
        candidates: ContextCandidate[];
      }>;
      ballot_measures: Array<{
        id: string;
        name: string;
        shortName?: string;
        streamlinedName?: string;
        districtName?: string;
        districtType?: string;
        type?: string;
        topics: string[];
        topicAreas: string[];
        summary?: string;
        yesVote?: string;
        noVote?: string;
        ballotQuestion?: string;
        ocdId?: string;
        status?: string;
        // Same provenance pair as ``ContextCandidate``: the citable Ballotpedia
        // link the measure record carries, and 'ballotpedia' when Democracy
        // Works did not return the measure at all.
        ballotpediaUrl?: string | null;
        source?: string;
      }>;
      voting_dates?: ContextVotingDates;
      polling_locations?: ContextPollingLocation[];
    }>;
    election_offices?: ContextElectionOffice[];
    // Current elected officials representing the voter's address (Ballotpedia
    // Phase 2). Mirrors the backend ``ContextElectionData.officeholders`` so
    // the agent's prompt builder grounds "who represents me" from context.
    officeholders?: ContextOfficeholder[];
    // The voter's in-progress picks (device-local, not persisted server-side
    // in this context object). Attached fresh on every ``getContext()`` call
    // by ``ContextService.getContext``, so a pick made mid-conversation
    // reaches the very next chat turn.
    user_picks?: {
      liked_candidate_ids: string[];
      measure_positions: Record<string, string>;
    };
    source_type?: string;
  };
}

export interface ContextInitResponse {
  context: ConversationContext;
}

export type ContextListener = (ctx: ConversationContext | null) => void;

export class ContextService {
  private static readonly STORAGE_KEY = 'vm_ctx';

  private static listeners = new Set<ContextListener>();

  /**
   * Subscribe to context updates. The listener fires whenever `initContext`
   * completes or `clearContext` is called, giving consumers (e.g. the chat
   * landing chips) a way to react to address changes after login.
   *
   * Returns an unsubscribe function.
   */
  static subscribe(listener: ContextListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private static notify(ctx: ConversationContext | null): void {
    this.listeners.forEach((l) => {
      try {
        l(ctx);
      } catch (err) {
        console.error('Context listener error:', err);
      }
    });
  }

  /**
   * Initialize conversation context by calling /api/context/init
   * and store it in sessionStorage
   */
  static async initContext(accessToken?: string): Promise<ConversationContext | null> {
    // Chat holds sends until this lands, so a turn can't go out on an empty or
    // previous ballot's context.
    return holdChatUntil(this.fetchAndStoreContext(accessToken));
  }

  private static async fetchAndStoreContext(
    accessToken?: string
  ): Promise<ConversationContext | null> {
    try {
      const response = await apiClient('/api/context/init', {
        method: 'POST',
        accessToken,
        failMessage: 'Context init failed',
      });

      const data: ContextInitResponse = await response.json();

      // Store in sessionStorage
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data.context));
      this.notify(data.context);

      return data.context;
    } catch (error) {
      console.error('Failed to initialize context:', error);
      // Clear any stale context on error
      this.clearContext();
      return null;
    }
  }

  /**
   * Get context from sessionStorage, with the voter's current ballot picks
   * layered onto ``election.user_picks``.
   *
   * Picks are read live from their own localStorage-backed stores on every
   * call (rather than baked into the context once at init time) so a like or
   * measure vote made mid-conversation reaches the very next chat turn's
   * context — those stores never round-trip through context init.
   */
  static getContext(): ConversationContext | null {
    try {
      const contextStr = sessionStorage.getItem(this.STORAGE_KEY);
      if (!contextStr) {
        return null;
      }
      const context = JSON.parse(contextStr) as ConversationContext;
      return {
        ...context,
        election: {
          ...context.election,
          user_picks: {
            liked_candidate_ids: getLikedCandidateIds(),
            measure_positions: getMeasureSelections(),
          },
        },
      };
    } catch (error) {
      console.error('Failed to parse context from sessionStorage:', error);
      this.clearContext();
      return null;
    }
  }

  /**
   * Set context directly without calling the backend. Used by the guest
   * flow — context is built client-side from the localStorage-cached
   * address + /ballot/by-address response, since guests can't authenticate
   * to /api/context/init. Subscribers are notified so the dynamic chips
   * and any other context-dependent UI refresh.
   *
   * Pass ``null`` to clear (equivalent to :meth:`clearContext`).
   *
   * ``JSON.stringify`` silently drops keys whose values are ``undefined``
   * (which is fine — Pydantic on the backend treats missing keys as
   * default/None for ``Optional`` fields). The try/catch mirrors the
   * ``initContext`` error swallow so a Safari-private-mode
   * ``QuotaExceededError`` can't take down whatever render path
   * triggered this call.
   */
  static setContext(ctx: ConversationContext | null): void {
    if (ctx === null) {
      this.clearContext();
      return;
    }
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(ctx));
      this.notify(ctx);
    } catch (error) {
      console.error('Failed to write context to sessionStorage:', error);
      // Fall back to clearing so subscribers see a coherent "no context"
      // state rather than a half-written one.
      this.clearContext();
    }
  }

  /**
   * Clear context from sessionStorage
   */
  static clearContext(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.notify(null);
  }

  /**
   * Check if context is available
   */
  static hasContext(): boolean {
    return this.getContext() !== null;
  }
}
